'use client';

import { useMemo } from 'react';

import { useIsHydrated } from '@/hooks/useIsHydrated';
import { useUrlQuery } from '@/hooks/useUrlQuery';
import { ELocalStorageKeys, EQueryParams } from '@/lib/enums';

import { useAppStateService } from '../AppStateContext';
import { useObservable } from '../observable';
import { webStorageUtil } from '../webStorage.state.service';

const CAMPAIGN_SOURCE = 'campaign';

export const useAppState = () => {
  const appStateService = useAppStateService();
  const isHydrated = useIsHydrated();
  const from = useUrlQuery(EQueryParams.FROM);
  const isFromCampaign = useMemo(() => from === CAMPAIGN_SOURCE, [from]);

  const openInfoModal = (url?: string) => {
    appStateService.setInfoModalUrl(url);
  };

  const closeInfoModal = () => {
    appStateService.setInfoModalUrl();
  };

  // Storage is the browser's: the server and the hydrating client both see no player.
  const playerData = isHydrated ? webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA) : null;

  return {
    initialBoot: useObservable(appStateService.initialBoot),
    campaignOfferData: useObservable(appStateService.campaignOfferData),
    deviceType: useObservable(appStateService.deviceType),
    platform: useObservable(appStateService.platform),
    design: useObservable(appStateService.design),
    currentPageName: useObservable(appStateService.currentPageName),
    domain: useObservable(appStateService.domain),
    activeOrderId: useObservable(appStateService.activeOrderId),
    setInitialBoot: appStateService.setInitialBoot,
    collectedProductsImages: useObservable(appStateService.collectedProductsImages),
    displayLoader: useObservable(appStateService.displayLoader),
    showConfetti: useObservable(appStateService.showConfetti),
    selectedOffer: useObservable(appStateService.selectedOffer),
    showPaymentModal: useObservable(appStateService.showPaymentModal),
    failedHashValidation: useObservable(appStateService.failedHashValidation),
    backgroundProps: useObservable(appStateService.backgroundProps),
    isProfileEnabled: useObservable(appStateService.isProfileEnabled),
    infoModalUrl: useObservable(appStateService.infoModalUrl),
    playerData,
    isGlobalLoading: useObservable(appStateService.isGlobalLoading),
    utms: useObservable(appStateService.utms),
    setCollectedProductsImages: appStateService.setCollectedProductsImages,
    setDisplayLoader: appStateService.setDisplayLoader,
    setShowConfetti: appStateService.setShowConfetti,
    setSelectedOffer: appStateService.setSelectedOffer,
    setShowPaymentModal: appStateService.setShowPaymentModal,
    setFailedHashValidation: appStateService.setFailedHashValidation,
    setBackgroundProps: appStateService.setBackgroundProps,
    setIsProfileEnabled: appStateService.setIsProfileEnabled,
    setUtms: appStateService.setUtms,
    openInfoModal,
    closeInfoModal,
    setIsGlobalLoading: appStateService.setIsGlobalLoading,
    setCampaignOfferData: appStateService.setCampaignOfferData,
    setActiveOrderId: appStateService.setActiveOrderId,
    setDeviceType: appStateService.setDeviceType,
    setPlatform: appStateService.setPlatform,
    setDesign: appStateService.setDesign,
    setCurrentPageName: appStateService.setCurrentPageName,
    setDomain: appStateService.setDomain,
    isFromCampaign,
    currentLanguage: useObservable(appStateService.currentLanguage),
    languagesList: useObservable(appStateService.languagesList),
    setCurrentLanguage: appStateService.setCurrentLanguage,
    setLanguagesList: appStateService.setLanguagesList
  };
};
